///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { DesignColors } from "@/earthbrowser/design-system/DesignColors"
import { PolygonGeometry } from "@/earthbrowser/entities/geometries/PolygonGeometry"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { SimpleNode } from "@/earthbrowser/entities/SimpleNode/SimpleNode"
import { geographic } from "@/earthbrowser/utils/geographic"
import { isMemoryConstrainedDevice } from "@/earthbrowser/utils/MemoryLog"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { trig } from "@stovokor/math/math"
import { vec2 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { simplifyRing } from "./simplifyRing"

// The outline floats slightly above the fill so it always wins the depth
// test against its own polygon
const cOutlineLiftKm = 0.15

const cOutlineWidth = 1.5
const cOutlineWidthFocused = 3

// Ring simplification tolerance: NIFC perimeters carry meter-level survey
// detail, invisible at the zooms where perimeters render. ~1px at typical
// inspection distance; doubled on memory-constrained devices.
const cSimplifyToleranceM = 30
const cSimplifyToleranceConstrainedM = 60

interface GeoJSONPolygon {
	type: "Polygon"
	coordinates: number[][][]
}

interface GeoJSONMultiPolygon {
	type: "MultiPolygon"
	coordinates: number[][][][]
}

export type WildfireGeometry = GeoJSONPolygon | GeoJSONMultiPolygon

/**
 * Filled perimeter polygon(s) plus outline for one wildfire, draped just
 * above the terrain. The globe renders real elevation, so the caller passes
 * an altitude (km above the ellipsoid) computed from the terrain under the
 * fire — a fixed sea-level altitude would bury the polygons under mountains.
 * Fill triangulation reuses PolygonGeometry (poly2tri); outlines are
 * screen-space constant-width polylines. Fills are best-effort: a
 * degenerate ring falls back to outline-only rather than dropping the fire.
 */
export class WildfirePerimeterNode extends NodeBase {
	// Aggregate memory diagnostics across all perimeter nodes; logged every
	// 100 nodes so the log sequence brackets a mobile out-of-memory kill
	private static liveNodes = 0
	private static createdTotal = 0
	private static fillVertexTotal = 0
	private static outlineVertexTotal = 0

	private readonly fills: SimpleNode[] = []
	private readonly outlines: PolylineNode[] = []
	private active: boolean
	private focused = false
	private fillVertices = 0
	private outlineVertices = 0

	constructor(context: StovokorContext, geometry: WildfireGeometry, active: boolean, altitudeKm: number) {
		super()
		this.active = active
		const fillAltitude = altitudeKm
		const outlineAltitude = altitudeKm + cOutlineLiftKm

		const polygons: number[][][][] =
			geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates

		const toleranceM = isMemoryConstrainedDevice()
			? cSimplifyToleranceConstrainedM
			: cSimplifyToleranceM

		for (const sourceRings of polygons) {
			const rings = sourceRings.map(r => simplifyRing(r, toleranceM))
			const outer = rings[0]
			if (!outer || outer.length < 4) continue

			// Filled interior from the outer ring (holes are rare in fire
			// perimeters and poly2tri hole support is disabled engine-wide)
			try {
				const ringVec2 = outer.map(([lon, lat]) => new vec2(lon, lat))
				const bounds = {
					minLon: Math.min(...outer.map(p => p[0])),
					minLat: Math.min(...outer.map(p => p[1])),
					maxLon: Math.max(...outer.map(p => p[0])),
					maxLat: Math.max(...outer.map(p => p[1])),
				}
				const fillGeometry = new PolygonGeometry(context, [ringVec2], bounds, fillAltitude)
				const fill = new SimpleNode(context, fillGeometry, this.fillColor())
				this.fills.push(fill)
				this.addNode(fill)
				// Unindexed triangles: (n - 2) triangles x 3 vertices
				this.fillVertices += Math.max(0, ringVec2.length - 2) * 3
			} catch {
				// poly2tri can throw on self-intersecting rings; keep the outline
			}

			// Outline every ring (outer + holes) so complex fires read correctly
			for (const ring of rings) {
				if (!ring || ring.length < 4) continue
				const positions = ring.map(([lon, lat]) =>
					new geographic(lon * trig.rad, lat * trig.rad, outlineAltitude).cartesian
				)
				const outline = new PolylineNode(context, {
					positions,
					width: cOutlineWidth,
					color: this.outlineColor(),
					loop: true,
				})
				this.outlines.push(outline)
				this.addNode(outline)
				this.outlineVertices += positions.length
			}
		}

		WildfirePerimeterNode.liveNodes++
		WildfirePerimeterNode.createdTotal++
		WildfirePerimeterNode.fillVertexTotal += this.fillVertices
		WildfirePerimeterNode.outlineVertexTotal += this.outlineVertices
		if (WildfirePerimeterNode.createdTotal % 100 === 0) {
			WildfirePerimeterNode.logStats()
		}
	}

	private static logStats(): void {
		const fillK = Math.round(WildfirePerimeterNode.fillVertexTotal / 1000)
		const outlineK = Math.round(WildfirePerimeterNode.outlineVertexTotal / 1000)
		console.log(
			`[WildfirePerimeter] ${WildfirePerimeterNode.liveNodes} live perimeters, `
			+ `${fillK}k fill vertices, ${outlineK}k outline vertices`
		)
	}

	override destroy(): void {
		WildfirePerimeterNode.liveNodes--
		WildfirePerimeterNode.fillVertexTotal -= this.fillVertices
		WildfirePerimeterNode.outlineVertexTotal -= this.outlineVertices
		super.destroy()
	}

	public updateStyle(active: boolean): void {
		this.active = active
		this.applyStyle()
	}

	/** Brighten fill and thicken outline while the fire is selected */
	public setFocused(focused: boolean): void {
		if (this.focused === focused) return
		this.focused = focused
		this.applyStyle()
	}

	private applyStyle(): void {
		const fillColor = this.fillColor()
		for (const fill of this.fills) {
			fill.color = fillColor
		}
		const outlineColor = this.outlineColor()
		const width = this.focused ? cOutlineWidthFocused : cOutlineWidth
		for (const outline of this.outlines) {
			outline.updateColor(outlineColor)
			outline.updateWidth(width)
		}
	}

	private fillColor() {
		if (this.focused) return DesignColors.FIRE_FILL_FOCUSED
		return this.active ? DesignColors.FIRE_FILL_ACTIVE : DesignColors.FIRE_FILL_CONTAINED
	}

	private outlineColor() {
		if (this.focused) return DesignColors.FIRE_OUTLINE_FOCUSED
		return this.active ? DesignColors.FIRE_OUTLINE_ACTIVE : DesignColors.FIRE_OUTLINE_CONTAINED
	}
}
